import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useCreateProduct, useUpdateProduct, useProduct } from "@/hooks/useInventory";
import { ProductFormData, ProductWithStock } from "@/types/inventory";

type ProductFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  productId?: string;
  initialData?: ProductWithStock;
};

export function ProductFormModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  productId,
  initialData,
}: ProductFormModalProps) {
  const [formData, setFormData] = useState<ProductFormData>({
    name: "",
    brand: "",
    form: "",
    dosage: "",
    default_selling_price: undefined,
    unit_of_measure: "units",
    low_stock_threshold: 10,
    description: "",
    instructions: "",
    is_active: true,
  });

  // Load existing data when in edit mode
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        brand: initialData.brand || "",
        form: initialData.form || "",
        dosage: initialData.dosage || "",
        default_selling_price: initialData.default_selling_price,
        unit_of_measure: initialData.unit_of_measure || "units",
        low_stock_threshold: initialData.low_stock_threshold || 10,
        description: initialData.description || "",
        instructions: initialData.instructions || "",
        is_active: initialData.is_active ?? true,
      });
    }
  }, [initialData]);

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct(productId || "");

  const mutation = productId ? updateProduct : createProduct;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || formData.name.trim().length === 0) {
      onError?.("Product name is required");
      return;
    }

    if (
      formData.default_selling_price !== undefined &&
      formData.default_selling_price < 0
    ) {
      onError?.("Selling price cannot be negative");
      return;
    }

    if (
      formData.low_stock_threshold !== undefined &&
      formData.low_stock_threshold < 0
    ) {
      onError?.("Low stock threshold cannot be negative");
      return;
    }

    try {
      // Clean up empty strings - convert to undefined for optional fields
      const cleanedData = {
        ...formData,
        brand: formData.brand || undefined,
        form: formData.form || undefined,
        dosage: formData.dosage || undefined,
        description: formData.description || undefined,
        instructions: formData.instructions || undefined,
      };

      await mutation.mutateAsync(cleanedData);
      onSuccess?.(
        productId
          ? "Product updated successfully"
          : "Product created successfully"
      );
      onClose();
    } catch (error: any) {
      onError?.(
        error.message ||
          `Failed to ${productId ? "update" : "create"} product`
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {productId ? "Edit Product" : "Add Product"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Omega-3 Fish Oil"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              required
            />
          </div>

          {/* Brand */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand
            </label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) =>
                setFormData({ ...formData, brand: e.target.value })
              }
              placeholder="e.g., Himalayan Organics"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            />
          </div>

          {/* Form & Dosage (side by side) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Form
              </label>
              <input
                type="text"
                value={formData.form}
                onChange={(e) =>
                  setFormData({ ...formData, form: e.target.value })
                }
                placeholder="e.g., Capsule"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dosage
              </label>
              <input
                type="text"
                value={formData.dosage}
                onChange={(e) =>
                  setFormData({ ...formData, dosage: e.target.value })
                }
                placeholder="e.g., 500mg"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
          </div>

          {/* Default Selling Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Selling Price (₹)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.default_selling_price || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  default_selling_price: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
              placeholder="e.g., 1200"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be the default price when assigning to clients
            </p>
          </div>

          {/* Unit of Measure & Low Stock Threshold (side by side) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit of Measure
              </label>
              <select
                value={formData.unit_of_measure}
                onChange={(e) =>
                  setFormData({ ...formData, unit_of_measure: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              >
                <option value="units">Units</option>
                <option value="bottles">Bottles</option>
                <option value="packets">Packets</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Low Stock Alert
              </label>
              <input
                type="number"
                min="0"
                value={formData.low_stock_threshold || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    low_stock_threshold: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                placeholder="10"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={2}
              placeholder="Product description..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
            />
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usage Instructions
            </label>
            <textarea
              value={formData.instructions}
              onChange={(e) =>
                setFormData({ ...formData, instructions: e.target.value })
              }
              rows={2}
              placeholder="General usage instructions..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
              className="w-4 h-4 text-[#5B8E87] border-gray-300 rounded focus:ring-[#5B8E87]"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Active (available for assignment)
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4A7069] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending
                ? "Saving..."
                : productId
                ? "Update Product"
                : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
